import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, Check, X, Eye, ArrowLeft, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';
import { createSafeRichHTML } from '../lib/sanitize';

export const EditorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'editor' && user.role !== 'admin') {
      toast.error('You need editor or admin access');
      navigate('/dashboard');
      return;
    }
    fetchPending();
  }, [user, navigate]);

  const fetchPending = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/articles/pending`,
        { withCredentials: true }
      );
      setArticles(res.data);
    } catch (error) {
      console.error('Error fetching pending articles:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (articleId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/articles/${articleId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success('Article approved and published');
      setArticles(articles.filter(a => a.article_id !== articleId));
      setSelectedArticle(null);
    } catch (error) {
      toast.error('Failed to approve article');
    }
  };

  const handleReject = async (articleId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/articles/${articleId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success('Article rejected (returned to draft)');
      setArticles(articles.filter(a => a.article_id !== articleId));
      setSelectedArticle(null);
    } catch (error) {
      toast.error('Failed to reject article');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <h2 className="text-4xl md:text-5xl tracking-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
          Pending Reviews
        </h2>
        <p className="text-muted-foreground mb-8">{articles.length} article{articles.length !== 1 ? 's' : ''} awaiting review</p>

        {articles.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-16 text-center" data-testid="no-pending">
            <Check size={48} weight="duotone" className="text-green-500 mx-auto mb-4" />
            <p className="text-lg mb-2">All caught up!</p>
            <p className="text-muted-foreground">No pending submissions to review</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Article List */}
            <div className="lg:col-span-1 space-y-3">
              {articles.map((article) => (
                <button
                  key={article.article_id}
                  onClick={() => setSelectedArticle(article)}
                  data-testid={`pending-article-${article.article_id}`}
                  className={`w-full text-left bg-card border rounded-lg p-4 transition-colors ${
                    selectedArticle?.article_id === article.article_id ? 'border-primary' : 'border-border hover:border-primary'
                  }`}
                >
                  <span className="text-xs font-semibold uppercase tracking-widest text-primary block mb-2">{article.category}</span>
                  <h4 className="font-semibold mb-2 line-clamp-2">{article.title}</h4>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <img src={article.author_picture || `https://ui-avatars.com/api/?name=${article.author_name}`} alt={article.author_name} className="w-5 h-5 rounded-full" />
                    <span>{article.author_name}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Article Preview */}
            <div className="lg:col-span-2">
              {selectedArticle ? (
                <div className="bg-card border border-border rounded-lg p-8" data-testid="article-preview">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={selectedArticle.author_picture || `https://ui-avatars.com/api/?name=${selectedArticle.author_name}`}
                      alt={selectedArticle.author_name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className="font-semibold text-sm">{selectedArticle.author_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Submitted {new Date(selectedArticle.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-3 block">
                    {selectedArticle.category}
                  </span>

                  <h3 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
                    {selectedArticle.title}
                  </h3>

                  {selectedArticle.meta_description && (
                    <p className="text-muted-foreground mb-6 italic border-l-4 border-primary pl-4">
                      {selectedArticle.meta_description}
                    </p>
                  )}

                  <div className="prose max-w-none mb-8 leading-relaxed text-lg" style={{ fontFamily: 'IBM Plex Sans, Mukta, sans-serif' }}
                       dangerouslySetInnerHTML={createSafeRichHTML(selectedArticle.content)}>
                  </div>

                  {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                      {selectedArticle.tags.map((tag, idx) => (
                        <span key={idx} className="text-xs bg-muted px-3 py-1 rounded-full">#{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3 pt-6 border-t border-border">
                    <button
                      onClick={() => handleApprove(selectedArticle.article_id)}
                      data-testid="approve-button"
                      className="flex items-center gap-2 bg-green-600 text-white rounded-full py-2.5 px-6 font-semibold hover:opacity-90 transition-opacity"
                    >
                      <Check size={20} weight="bold" />
                      Approve & Publish
                    </button>
                    <button
                      onClick={() => handleReject(selectedArticle.article_id)}
                      data-testid="reject-button"
                      className="flex items-center gap-2 border border-border text-foreground rounded-full py-2.5 px-6 font-semibold hover:bg-muted transition-colors"
                    >
                      <X size={20} weight="bold" />
                      Return to Draft
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg p-16 text-center h-full flex items-center justify-center">
                  <div>
                    <Eye size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Select an article to review</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorDashboard;
